/* eslint-disable stylistic/comma-dangle */
/**
 * King Living MegaMenu Theme
 *
 * Uses variant system for consistent styling with Header:
 * - variant="light": Black nav items (for transparent, solid states)
 * - variant="dark": White nav items (for gradient state)
 *
 * @override Extends Chakra UI theme
 */

import {A11Y_ICON_GLYPH_SIZE, A11Y_ICON_TARGET_MIN} from '@amblique/king-living/app/theme/foundations/a11y-targets';

/**
 * Get variant-specific styles for nav items
 * @param {string} color - Color token for nav items
 * @returns {Object} Variant styles
 */
const getNavVariantStyles = (color) => ({
  desktopTriggerButton: {
    color,
  },
});

const MegaMenu = {
  parts: [
    // Desktop styles
    'megaMenuDesktopWrapper',
    'megaMenuDesktopNav',
    'menuItemDesktopContainer',
    'desktopTriggerButton',
    'desktopPopoverContent',
    'megaMenuLoadingSpinner',
    'saleBadge',
    // Mobile styles
    'mobileDrawerHeader',
    'mobileDrawerOverlay',
    'mobileDrawerContent',
    'closeButton',
    'accountNameBadge',
    'logoMobileWrapper',
    'mobileDrawerLogoButton',
    'logoMobile',
    'mobileDrawerBody',
    'mobileDrawerBodyInner',
    'mobileSubcategoryPanel',
    'mobileSubcategoryPanelContent',
    'mobileSubcategoryPanelWrapper',
    'mobileSubcategoryPanelWrap',
    'mobileSubmenuItemsWrapper',
    'mobileSubmenuItem',
    'mobilePanelItemsList',
    'backButtonIcon',
    'shopAllTitleIcon',
    'mobileDrawerFooter',
    'mobileDrawerFooterStack',
    'mobileDrawerFooterRow',
    'mobileMenuItemParentWrapper',
    'mobileDrawerFooterIconButton',
    'mobileDrawerLocaleSelector',
    'backButton',
    'backButtonWrapper',
    'menuItemIconButton',
    // Shared styles
    'menuItemLink',
    'menuItemButton',
    'menuItemText',
    'menuItemContainer',
    'menuItemIcon',
    'menuItemImages',
    'menuItemDesktopGrid',
    'menuItemDesktopImage',
    'navWrapper',
    'navigationColumnWrapper',
    'navigationColumnLinksWrapper',
    'navigationColumnTitle',
    'navigationColumnTitleSkeleton',
    'navigationColumnLinkSkeleton',
    'shopAllLinkWrapper',
  ],

  baseStyle: (props = {}) => {
    const {level = 0, isDesktop, isOpen, isSearchOpen, popoverTop, headerState} = props;

    // Level-based text styles
    const getTextStyles = () => {
      switch (level) {
        case 0:
          return {
            color: 'brandColors.itemMenu',
            fontFeatureSettings: "'lnum' 1, 'pnum' 1",
            fontSize: '20px',
            fontWeight: '500',
            lineHeight: 1.2,
          };
        case 1:
          return {
            color: 'brandColors.itemMenu',
            fontFeatureSettings: "'lnum' 1, 'pnum' 1",
            fontSize: '18px',
            fontWeight: '500',
            lineHeight: 1.2,
          };
        default:
          return {
            fontSize: '20px',
            fontWeight: '500',
          };
      }
    };

    return {
      // Desktop styles
      megaMenuDesktopWrapper: {
        width: '100%',
      },
      megaMenuDesktopNav: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        minHeight: '56px', // Reserve space to prevent shift when loading
        paddingBottom: '0',
        borderBottom: '1px solid',
        // Border color based on header state:
        // - transparent or solid: brandColors.greyscale20
        // - gradient: whiteAlpha.450
        // When hovering header, gradient/transparent transform to solid (keeps greyscale20)
        borderColor: headerState === 'gradient' ?
          'whiteAlpha.450' :
          'brandColors.greyscale20',
        transition: 'border-color 0.2s ease',
        pointerEvents: isSearchOpen ? 'none' : 'auto',
      },
      menuItemDesktopContainer: {
        maxWidth: '1440px',
        mx: 'auto',
        paddingX: {base: '20px', lg: '80px'},
        paddingTop: {base: '0', lg: '45px'},
        paddingBottom: {base: '0', lg: '84px'},
        width: '100%',
      },
      // Desktop mega-menu: 5-col CSS Grid — nav-wrapper + one cell per image.
      menuItemDesktopGrid: {
        alignItems: 'start',
        display: 'grid',
        gap: 6,
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        width: '100%',
      },
      navWrapper: {
        alignItems: 'start',
        display: 'grid',
        gap: 'inherit',
        minWidth: 0,
        width: '100%',
        sx: {
          '& > .navigation-column': {
            marginBottom: 0,
            minWidth: 0,
            width: '100%',
          },
        },
      },
      menuItemDesktopImage: {
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        minWidth: 0,
        width: '100%',
        sx: {
          '& .chakra-link, & img, & .chakra-image': {
            display: 'block',
            maxWidth: '100%',
            width: '100%',
          },
          '& .chakra-text': {
            color: 'greyscale90Alpha.90',
            fontFeatureSettings: "'lnum' 1, 'pnum' 1",
            fontSize: '16px',
            fontWeight: 500,
            letterSpacing: 0,
            lineHeight: 1.2,
            transition: 'color 0.2s ease',
          },
          '& .chakra-link': {
            borderRadius: '2px',
            cursor: 'pointer',
            display: 'block',
            transition: 'opacity 0.2s ease',
            _focusVisible: {
              boxShadow: 'none',
              outline: '2px solid',
              outlineColor: 'brandColors.primaryDarkLinen',
              outlineOffset: '2px',
            },
          },
          '& .chakra-link:hover .chakra-image, & .chakra-link:hover img': {
            opacity: 0.85,
          },
          '&:has(.chakra-link):hover .image__description, &:has(.chakra-link):hover .image__description .chakra-text, &:has(.chakra-link):hover > .chakra-text': {
            color: 'brandColors.primaryDarkLinen',
          },
          '& .chakra-link:focus-visible ~ .image__description, & .chakra-link:focus-visible ~ .image__description .chakra-text, & .chakra-link:focus-visible ~ .chakra-text': {
            color: 'brandColors.primaryDarkLinen',
          },
        },
      },
      desktopTriggerButton: {
        // Color comes from variant (light/dark)
        transition: 'color 0.3s ease',
        _hover: {
          opacity: 0.7,
          _after: {
            backgroundColor: 'brandColors.primaryDarkLinen', // Show dark underline on hover for all items
          },
        },
        alignItems: 'center',
        display: 'flex',
        fontSize: '16px',
        fontWeight: '500',
        position: 'relative',
        textDecoration: 'none', // Prevent link underline
        // Active indicator pseudo-element
        _after: {
          content: '""',
          position: 'absolute',
          bottom: '-17px',
          left: 0,
          right: 0,
          height: '2px',
          width: 'calc(100% + 10px)',
          backgroundColor: isOpen ? 'brandColors.primaryDarkLinen' : 'transparent',
          transition: 'background-color 0.2s ease',
        },
        sx: {
          '.menu-item-wrapper .chakra-link': {
            bg: 'transparent',
            border: 'none',
            borderRadius: 0,
            color: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            lineHeight: 'inherit',
            p: 0,
            _hover: {
              bg: 'transparent',
              border: 'none',
              color: 'inherit',
              textDecoration: 'none',
            },
          },
          '.chakra-link': {
            _hover: {
              textDecoration: 'none',
            },
          },
        },
      },
      desktopPopoverContent: {
        width: '100vw',
        maxWidth: '100vw',
        minWidth: '100vw',
        left: '0 !important',
        right: '0 !important',
        marginLeft: '0 !important',
        marginRight: '0 !important',
        marginTop: '0 !important',
        paddingTop: '0 !important',
        padding: 0,
        transform: 'none !important',
        // Below search modal (1490) when search is open; otherwise below header chrome (sticky = 1100).
        zIndex: isSearchOpen ? 1 : 1099,
        pointerEvents: isSearchOpen ? 'none' : 'auto',
        position: 'fixed',
        // Dynamic top positioning based on trigger element's bottom
        top: isDesktop && popoverTop > 0 ? `${popoverTop}px !important` : 'auto',
        // Remove default Chakra popover styles that cause flash
        backgroundColor: 'background.primary',
        boxShadow: 'none',
        border: 'none',
        borderRadius: 0,
      },

      megaMenuLoadingSpinner: {
        color: 'brandColors.primaryDarkLinen',
        size: 'md',
      },

      saleBadge: {
        display: 'inline-block',
        backgroundColor: '#BF1619',
        borderRadius: 'md',
        height: '10px',
        width: '10px',
        marginRight: '13px',
      },

      // Mobile styles
      mobileDrawerHeader: {
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'brandColors.greyscale10',
        boxShadow: 'none',
        display: 'flex',
        justifyContent: 'space-between',
        padding: '20px',
      },
      mobileDrawerOverlay: {
        zIndex: 1098,
      },
      mobileDrawerContent: {
        backgroundColor: 'background.primary',
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        maxHeight: '100dvh',
        overflow: 'hidden',
        zIndex: 1099,
      },
      closeButton: {
        alignItems: 'center',
        display: 'inline-flex',
        height: A11Y_ICON_TARGET_MIN,
        justifyContent: 'center',
        minHeight: A11Y_ICON_TARGET_MIN,
        minWidth: A11Y_ICON_TARGET_MIN,
        width: A11Y_ICON_TARGET_MIN,
        sx: {
          svg: {
            height: A11Y_ICON_GLYPH_SIZE,
            width: A11Y_ICON_GLYPH_SIZE,
          },
        },
      },
      accountNameBadge: {
        backgroundColor: 'white',
        borderRadius: '50px',
        color: 'brandColors.greyscale80',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: A11Y_ICON_TARGET_MIN,
        height: A11Y_ICON_TARGET_MIN,
        fontSize: '11px',
        fontWeight: 500,
        minWidth: A11Y_ICON_TARGET_MIN,
        minHeight: A11Y_ICON_TARGET_MIN,
      },
      accountNameBadgeNotLoggedIn: {
        backgroundColor: 'transparent',
        width: 'auto',
        gap: '16px',
        sx: {
          '.chakra-button': {
            gap: '16px',
            width: 'auto',
          },
          '.chakra-button__icon': {
            marginRight: '0',
            width: A11Y_ICON_TARGET_MIN,
          },
          '.chakra-icon': {
            color: 'brandColors.primaryDarkLinen',
            height: A11Y_ICON_GLYPH_SIZE,
            width: A11Y_ICON_GLYPH_SIZE,
          },
        }
      },
      logoMobile: {
        width: '100%',
        height: 'auto',
      },
      logoMobileWrapper: {
        display: 'flex',
        maxWidth: {base: '76px', lg: '112px'},
        maxHeight: {base: '17px', lg: '24px'},
      },
      mobileDrawerLogoButton: {
        marginLeft: '9px',
        minWidth: 'auto',
        padding: 0,
      },
      mobileDrawerBody: {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        overscrollBehavior: 'contain',
        padding: '0',
        position: 'relative',
      },
      mobileDrawerBodyInner: {
        flex: '1 1 auto',
        height: '100%',
        minHeight: 0,
        position: 'relative',
        width: '100%',
        willChange: 'transform',
      },
      mobileSubcategoryPanel: {
        backgroundColor: 'background.primary',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 20px',
        paddingBottom: 'calc(24px + env(safe-area-inset-bottom))', // Account for safe area
        height: '100%',
        minHeight: 0, // Allow flex children to shrink
        left: 0,
        position: 'absolute',
        top: 0,
        transition: 'transform 0.3s ease-in-out',
        width: '100%',
        willChange: 'transform',
        boxSizing: 'border-box',
      },
      mobileDrawerLocaleSelector: {
        sx: {
          '.chakra-button': {
            width: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          },
          // Figma Icon/Black/Globe — Primary/Dark Linen (#8C683B); language label uses itemMenu
          '& .chakra-button svg': {
            color: 'brandColors.primaryDarkLinen',
          },
          // KINDW-1215: Figma mobile side nav — language code matches footer row typography
          '.chakra-button .chakra-text': {
            color: 'brandColors.itemMenu',
            fontSize: '20px',
            fontWeight: 500,
            lineHeight: '1.2',
          },
        },
      },
      mobileSubcategoryPanelContent: {
        flex: 1,
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        sx: {
          WebkitOverflowScrolling: 'touch',
        },
      },
      mobileSubcategoryPanelWrapper: {
        flex: 1,
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        width: 'calc(100% + 20px)',
        sx: {
          WebkitOverflowScrolling: 'touch',
        },
      },
      mobileSubcategoryPanelWrap: {
        display: 'flex',
        flexDirection: 'column',
        gap: '28px',
        width: '100%',
      },
      mobileSubmenuItemsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
        width: '100%',
      },
      mobileSubmenuItem: {
        paddingRight: '20px',
      },
      mobilePanelItemsList: {
        padding: '0',
        spacing: 0,
        width: '100%',
      },

      mobileDrawerFooter: {
        padding: '20px 0 calc(4px + env(safe-area-inset-bottom)) 0', // Add safe area padding
        flexShrink: 0, // Prevent footer from shrinking
        marginTop: 'auto', // Push footer to bottom
      },

      mobileDrawerFooterStack: {
        alignItems: 'flex-start',
        spacing: 0,
      },
      mobileDrawerFooterRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        columnGap: '16px',
        sx: {
          '.chakra-button': {
            height: A11Y_ICON_TARGET_MIN,
            width: A11Y_ICON_TARGET_MIN,
            minWidth: A11Y_ICON_TARGET_MIN,
            minHeight: A11Y_ICON_TARGET_MIN,
          },
          '.account-name-badge-button': {
            gap: '16px',
            width: 'auto',
          },
          '.mobile-drawer-footer-text': {
            color: 'brandColors.itemMenu',
            fontSize: '20px',
            fontWeight: 500,
            lineHeight: '150%',
          },
        }
      },
      mobileDrawerFooterIconButton: {
        alignItems: 'center',
        color: 'brandColors.primaryDarkLinen',
        display: 'inline-flex',
        height: A11Y_ICON_TARGET_MIN,
        justifyContent: 'center',
        minHeight: A11Y_ICON_TARGET_MIN,
        minWidth: A11Y_ICON_TARGET_MIN,
        width: A11Y_ICON_TARGET_MIN,
        lineHeight: '120%',
        sx: {
          '& svg': {
            width: A11Y_ICON_GLYPH_SIZE,
            height: A11Y_ICON_GLYPH_SIZE,
          },
          '&.account-name-badge': {
            backgroundColor: 'whiteAlpha.800',
            color: 'brandColors.greyscale80',
            fontSize: '11px',
          }
        }
      },
      mobileMenuItemParentWrapper: {
        alignItems: 'center',
        color: 'brandColors.itemMenu',
        cursor: 'pointer',
        display: 'flex',
        fontSize: '20px',
        gap: '20px',
        lineHeight: 1.2,
        padding: '12px',
        textDecoration: 'none',
        width: '100%',
      },

      menuItemIconButton: {
        alignItems: 'center',
        bg: 'transparent',
        border: 'none',
        display: 'flex',
        flexShrink: 0,
        height: '13px',
        justifyContent: 'center',
        minWidth: '7px',
        p: 0,
        width: '7px',
      },
      // Shared styles
      menuItemLink: {
        alignItems: 'center',
        color: 'brandColors.greyscale90',
        justifyContent: 'space-between',
        textDecoration: 'none',
        width: '100%',
        _hover: {
          color: 'brandColors.primaryDarkLinen',
          textDecoration: 'underline',
        },
      },

      menuItemButton: {
        alignItems: 'center',
        bg: 'transparent',
        border: 'none',
        cursor: 'pointer',
        justifyContent: 'space-between',
        p: 0,
        textAlign: 'left',
        width: '100%',
      },

      menuItemText: {
        ...getTextStyles(),
        flex: '1 0 0',
        minWidth: 0,
        wordBreak: 'break-word',
      },

      menuItemContainer: {
        marginBottom: {base: 0, lg: '32px'},
      },

      menuItemIcon: {
        boxSize: 'auto',
        color: '#1B1A18',
        flexShrink: 0,
        height: '13px',
        width: '7px',
      },

      menuItemImages: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        gap: {base: '4px', lg: '36px'},
        mt: 0,
        overflowX: {base: 'auto', lg: 'visible'},
        sx: {
          '.menu-item-image': {
            minWidth: {base: '258px', lg: 'auto'},
          },
          '&.menu-item-images__mobile': {
            marginLeft: '-20px',
            marginRight: '-20px',
            marginTop: 0,
            paddingBottom: '2px',
            paddingLeft: '20px',
            scrollPaddingLeft: '20px',
            width: 'calc(100% + 40px)',
          },
          '&.menu-item-images__mobile .menu-item-image': {
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            gap: '14px',
            minWidth: '258px',
            width: '258px',
          },
          '&.menu-item-images__mobile .menu-item-image .chakra-link, &.menu-item-images__mobile .menu-item-image img, &.menu-item-images__mobile .menu-item-image .chakra-image': {
            display: 'block',
            height: 'auto',
            objectFit: 'cover',
            width: '100%',
          },
          '&.menu-item-images__mobile .menu-item-image .chakra-text, &.menu-item-images__mobile .menu-item-image [class*="description"]': {
            marginTop: 0,
            paddingX: 0,
          },
          '&.menu-item-images__desktop': {
            marginLeft: 'auto',
          },
          '&.menu-item-images__desktop .menu-item-image': {
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            gap: '14px',
            maxWidth: {lg: '258px'},
            width: {lg: '258px'},
          },
          '&.menu-item-images__desktop .menu-item-image .chakra-link, .menu-item-images__desktop .menu-item-image img, .menu-item-images__desktop .menu-item-image .chakra-image': {
            display: {lg: 'block'},
            maxWidth: {lg: '100%'},
            width: {lg: '100%'},
          },
          '&.menu-item-images__mobile .menu-item-image .chakra-text': {
            color: 'brandColors.greyscale90',
            fontSize: '18px',
            fontWeight: 500,
            transition: 'color 0.2s ease',
          },
          '&.menu-item-images__desktop .menu-item-image .chakra-text': {
            color: 'greyscale90Alpha.90',
            fontFeatureSettings: "'lnum' 1, 'pnum' 1",
            fontSize: '16px',
            fontWeight: 500,
            letterSpacing: 0,
            lineHeight: 1.2,
            transition: 'color 0.2s ease',
          },
          // Linked promo tiles — match nav link hover (primaryDarkLinen); no layout shift (opacity/outline only).
          '& .menu-item-image .chakra-link': {
            borderRadius: '2px',
            cursor: 'pointer',
            display: 'block',
            transition: 'opacity 0.2s ease',
            _focusVisible: {
              boxShadow: 'none',
              outline: '2px solid',
              outlineColor: 'brandColors.primaryDarkLinen',
              outlineOffset: '2px',
            },
          },
          '& .menu-item-image .chakra-link:hover .chakra-image, & .menu-item-image .chakra-link:hover img': {
            opacity: 0.85,
          },
          '& .menu-item-image:has(.chakra-link):hover .image__description, & .menu-item-image:has(.chakra-link):hover .image__description .chakra-text, & .menu-item-image:has(.chakra-link):hover > .chakra-text': {
            color: 'brandColors.primaryDarkLinen',
          },
          '& .menu-item-image .chakra-link:focus-visible ~ .image__description, & .menu-item-image .chakra-link:focus-visible ~ .image__description .chakra-text, & .menu-item-image .chakra-link:focus-visible ~ .chakra-text': {
            color: 'brandColors.primaryDarkLinen',
          },
        },
      },
      // Navigation column styles
      navigationColumnWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginBottom: {base: 0, lg: '16px'},
        minWidth: {base: 'auto', lg: '160px'},
        width: {base: '100%', lg: 'auto'},
      },
      navigationColumnLinksWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: {base: '18px', lg: '16px'},
        sx: {
          '& .chakra-link.link--outlined-light, & .chakra-link.link--filled-light, & .chakra-link.link--outlined-dark, & .chakra-link.link--filled-dark': {
            width: 'fit-content',
          },
          '.chakra-link:not(.link--outlined-light):not(.link--filled-light):not(.link--outlined-dark):not(.link--filled-dark), .chakra-link:not(.link--outlined-light):not(.link--filled-light):not(.link--outlined-dark):not(.link--filled-dark) > .chakra-text': {
            color: 'brandColors.itemMenu',
            fontFeatureSettings: "'lnum' 1, 'pnum' 1",
            fontSize: {base: '18px', lg: '14px'},
            fontWeight: 500,
            lineHeight: 1.2,
            textDecoration: 'none',
          },
          '.chakra-link:not(.link--outlined-light):not(.link--filled-light):not(.link--outlined-dark):not(.link--filled-dark):hover, .chakra-link:not(.link--outlined-light):not(.link--filled-light):not(.link--outlined-dark):not(.link--filled-dark):hover > .chakra-text': {
            color: 'brandColors.primaryDarkLinen',
            textDecoration: 'none',
          },
          '.link--outlined-light:hover .chakra-text, .link--filled-light:hover .chakra-text, .link--outlined-dark:hover .chakra-text, .link--filled-dark:hover .chakra-text': {
            color: 'inherit',
          },
        },
      },
      navigationColumnTitle: {
        color: 'brandColors.primaryDarkLinen',
        fontSize: '10px',
        fontWeight: 600,
        letterSpacing: '2px',
        lineHeight: 'normal',
        textTransform: 'uppercase',
        width: '100%',
      },

      navigationColumnTitleSkeleton: {
        borderRadius: '2px',
      },

      navigationColumnLinkSkeleton: {
        borderRadius: '2px',
      },
      backButton: {
        color: 'brandColors.itemMenu',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      },
      backButtonIcon: {
        boxSize: 'auto',
        color: '#1B1A18',
        flexShrink: 0,
        height: '13px',
        width: '8px',
      },
      backButtonWrapper: {
        alignItems: 'center',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-start',
        padding: 0,
        width: 'auto',
      },
      shopAllLinkWrapper: {
        marginBottom: {base: 0, lg: '0'},
        marginTop: {base: 0, lg: '40px'},
        sx: {
          'a': {
            alignItems: 'center',
            color: {base: 'brandColors.itemMenu', lg: 'black'},
            display: 'flex',
            fontFeatureSettings: "'lnum' 1, 'pnum' 1",
            fontSize: {base: '20px', lg: '14px'},
            fontWeight: {base: 500, lg: 600},
            gap: '12px',
            lineHeight: 1.2,
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          },
          '.chakra-icon': {
            color: 'brandColors.primaryDarkLinen',
            height: {base: '13px', lg: '16px'},
            width: {base: '15px', lg: '16px'},
          },
        },
      },
      shopAllTitleIcon: {
        color: 'brandColors.primaryDarkLinen',
        flexShrink: 0,
        height: '13px',
        width: '15px',
      },
    };
  },

  /**
   * Variants for nav item color scheme (shared with Header)
   * - light: Dark nav items (for transparent, solid states)
   * - dark: White nav items (for gradient state)
   */
  variants: {
    light: getNavVariantStyles('brandColors.greyscale90'),
    dark: getNavVariantStyles('text.white'),
  },

  /**
   * Default variant (light = dark text)
   */
  defaultProps: {
    variant: 'light',
  },
};

export default MegaMenu;
